import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import YoutubeIcon from '../../Image/YoutubeIcon.png';

let youtubeApiPromise = null;

// 유튜브 IFrame API 로드 //
function loadYoutubeApi() {
  if (window.YT !== undefined && window.YT.Player !== undefined) return Promise.resolve(window.YT);
  if (youtubeApiPromise !== null) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    const prevCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      if (typeof prevCallback === 'function') prevCallback();

      resolve(window.YT);
    }

    const script = document.createElement('script');

    script.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(script);
  });

  return youtubeApiPromise;
}

// 제출 영상 URL 에서 재생목록 ID 와 영상 ID 추출 //
function decideVideoSource(videoUrl) {
  let parsedUrl = undefined;

  try {
    parsedUrl = new URL(videoUrl.replaceAll(' ', ''));
  }
  catch (error) {
    return { playlistId: '', videoId: '' };
  }

  const playlistId = parsedUrl.searchParams.get('list');
  const watchVideoId = parsedUrl.searchParams.get('v');

  let videoId = watchVideoId === null ? '' : watchVideoId;

  if (videoId === '' && parsedUrl.hostname.includes('youtu.be')) videoId = parsedUrl.pathname.replace('/', '');
  else if (videoId === '' && parsedUrl.pathname.includes('/shorts/')) videoId = parsedUrl.pathname.replace('/shorts/', '');

  return { playlistId: playlistId === null ? '' : playlistId, videoId: videoId };
}

// 플레이어 생성 옵션 정리 //
function decidePlayerOptions(videoUrl, events) {
  const source = decideVideoSource(videoUrl);

  const options = {
    width: '100%',
    height: '100%',
    playerVars: { origin: window.location.origin },
    events: events
  };

  if (source.videoId !== '') options.videoId = source.videoId;

  if (source.playlistId !== '') {
    options.playerVars.list = source.playlistId;

    if (source.videoId === '') options.playerVars.listType = 'playlist';
  }

  return options;
}

export default function PlaylistVideoBox(props) {
  const { videoUrl, marginTop, width, height, borderRadius, thumbnailColor, noticeColor, noticeText, buttonText } = props;

  const [videoIds, setVideoIds] = useState([]);
  const [clickedIndex, setClickedIndex] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);

  const PlayerRef = useRef(null);
  const ContainerRef = useRef();

  // 제출 영상 플레이어 생성 //
  useEffect(() => {
    let player = undefined;
    let isCanceled = false;

    setVideoIds([]);
    setClickedIndex(0);
    setIsBlocked(false);

    loadYoutubeApi().then((YT) => {
      if (isCanceled || ContainerRef.current === undefined) return;

      const holder = document.createElement('div');

      ContainerRef.current.appendChild(holder);

      player = new YT.Player(holder, decidePlayerOptions(videoUrl, {
        onReady: onReadyPlayer,
        onError: onErrorPlayer,
        onStateChange: onChangePlayerState
      }));

      PlayerRef.current = player;
    });

    return () => {
      isCanceled = true;
      PlayerRef.current = null;

      if (player === undefined) return;

      try {
        player.destroy();
      }
      catch (error) {
      }
    }
  }, [videoUrl])

  // 플레이어 준비 완료시 //
  function onReadyPlayer(e) {
    const playlist = e.target.getPlaylist();

    if (playlist === null || playlist === undefined || playlist.length === 0) return;

    setVideoIds(playlist);
    setClickedIndex(e.target.getPlaylistIndex());
  }

  // 플레이어 오류 발생시 //
  function onErrorPlayer() {
    setIsBlocked(true);
  }

  // 플레이어 재생 상태 변경시 //
  function onChangePlayerState(e) {
    const index = e.target.getPlaylistIndex();

    if (index === -1) return;

    setClickedIndex(index);
  }

  // 유튜브로 이동 버튼 클릭시 //
  function onClickYoutubeButton() {
    window.open(videoUrl, '_blank', 'noopener,noreferrer');
  }

  // 재생목록 썸네일 클릭시 //
  function onClickThumbnail(index) {
    if (PlayerRef.current === null) return;

    PlayerRef.current.playVideoAt(index);
    setClickedIndex(index);
  }

  // 재생목록 썸네일 정리 //
  function renderThumbnails() {
    return videoIds.map((videoId, index) => (
      <ThumbnailBox
        key={'PlaylistThumbnail-' + index}
        isClicked={index === clickedIndex}
        thumbnailColor={thumbnailColor}
        borderRadius={borderRadius}
        onClick={() => onClickThumbnail(index)}>
        <ThumbnailImage src={'https://img.youtube.com/vi/' + videoId + '/mqdefault.jpg'} loading='lazy' />
        <IndexBox isClicked={index === clickedIndex} borderRadius={borderRadius}>
          <Text fontFamily='Poppins-Medium' fontSize={10} color='#FFFFFF' cursor='pointer'>{index + 1}</Text>
        </IndexBox>
      </ThumbnailBox>
    ));
  }

  return (
    <TopLevelWrapper marginTop={marginTop} width={width}>
      <VideoViewBox ref={ContainerRef} width={width} height={height} borderRadius={borderRadius} />
      {
        isBlocked &&
        <NoticeBox>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={noticeColor} cursor='default'>{noticeText}</Text>
        </NoticeBox>
      }
      {
        videoIds.length > 1 &&
        <ThumbnailStripWrapper thumbnailColor={thumbnailColor}>
          {renderThumbnails()}
        </ThumbnailStripWrapper>
      }
      {
        (buttonText !== undefined && buttonText !== '') &&
        <YoutubeButton
          thumbnailColor={thumbnailColor}
          borderRadius={borderRadius}
          onClick={onClickYoutubeButton}>
          <YoutubeIconImage src={YoutubeIcon} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={noticeColor} cursor='pointer'>{buttonText}</Text>
        </YoutubeButton>
      }
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: ${props => props.marginTop};

  width: ${props => props.width};
`;

const VideoViewBox = styled.div`
  position: relative;

  width: ${props => props.width};
  height: ${props => props.height};
  aspect-ratio: 16 / 9;

  border-radius: ${props => props.borderRadius};
  background-color: transparent;

  overflow: hidden;

  iframe {
    position: absolute;

    top: 0px;
    left: 0px;

    width: 100%;
    height: 100%;

    border: 0px;
  }
`;

const NoticeBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 10px;

  width: 100%;
`;

const ThumbnailStripWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  margin-top: 10px;

  width: 100%;

  gap: 8px;

  overflow-x: auto;

  ::-webkit-scrollbar {
    height: 4px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: ${props => props.thumbnailColor};
  }

  ::-webkit-scrollbar-track {
    background-color: transparent;
  }
`;

const ThumbnailBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex-shrink: 0;

  width: 96px;
  height: 54px;

  border: 2px solid ${props => props.isClicked ? `#6DD49E` : props.thumbnailColor};
  border-radius: ${props => props.borderRadius};
  background-color: ${props => props.thumbnailColor};

  opacity: ${props => props.isClicked ? `1` : `0.6`};

  overflow: hidden;

  :hover {
    cursor: pointer;
    opacity: 1;
  }

  transition: all 0.3s ease-in-out;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;
`;

const IndexBox = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  top: 0px;
  left: 0px;

  width: 18px;
  height: 16px;

  border-radius: 0px 0px ${props => props.borderRadius} 0px;
  background-color: ${props => props.isClicked ? `#6DD49E` : `rgba(0, 0, 0, 0.6)`};
`;

const YoutubeButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 10px;
  padding: 6px 0px;

  width: 100%;

  gap: 6px;

  border-radius: ${props => props.borderRadius};
  background-color: ${props => props.thumbnailColor};

  :hover {
    cursor: pointer;
    opacity: 0.8;
  }

  transition: all 0.3s ease-in-out;
`;

const YoutubeIconImage = styled.img`
  width: 20px;
  height: 20px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  :hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;
